import React, { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Newspaper, TrendingUp, AlertTriangle, Globe, Clock, 
  Bookmark, Share2, ExternalLink, Filter, Search, Sparkles,
  Factory, Cpu, Zap, ChevronRight
} from 'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { useTranslation } from 'react-i18next';

const IntelligenceFeed = () => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('all');

  const newsItems = [
    {
      id: 1,
      category: 'Supply Chain',
      title: 'TSMC Announces $40B Investment in Arizona Fab Expansion',
      summary: 'Major capacity increase expected to ease chip shortage by 2026. This will significantly impact availability of advanced node semiconductors for automotive and consumer electronics.',
      source: 'Reuters',
      time: '2 hours ago',
      impact: 'Positive',
      relevantComponents: ['MCU', 'Power IC', 'Analog IC'],
      saved: false,
      featured: true
    },
    {
      id: 2,
      category: 'Market Trends',
      title: 'MLCC Prices Expected to Rise 15-20% in Q2 2025',
      summary: 'Industry analysts predict ceramic capacitor shortage due to increased EV production and 5G infrastructure deployment.',
      source: 'EE Times',
      time: '5 hours ago',
      impact: 'Warning',
      relevantComponents: ['Capacitors', 'MLCC'],
      saved: true,
      featured: false
    },
    {
      id: 3,
      category: 'Regulatory',
      title: 'New EU Regulations on Conflict Minerals Take Effect',
      summary: 'Companies must now demonstrate supply chain due diligence for tin, tantalum, tungsten, and gold sourcing.',
      source: 'European Commission',
      time: '1 day ago',
      impact: 'Neutral',
      relevantComponents: ['All'],
      saved: false,
      featured: false
    },
    {
      id: 4,
      category: 'Technology',
      title: 'STMicroelectronics Launches New STM32U5 Ultra-Low Power MCU',
      summary: 'New microcontroller family offers 50% power reduction compared to previous generation, targeting IoT applications.',
      source: 'STMicroelectronics',
      time: '1 day ago',
      impact: 'Positive',
      relevantComponents: ['MCU', 'STM32'],
      saved: false,
      featured: true
    },
    {
      id: 5,
      category: 'Supply Chain',
      title: 'Japan Earthquake Disrupts Murata Capacitor Production',
      summary: 'Temporary production halt at Fukui facility may cause 2-3 week delivery delays for ceramic capacitors.',
      source: 'Nikkei',
      time: '2 days ago',
      impact: 'Warning',
      relevantComponents: ['Capacitors', 'MLCC', 'Inductors'],
      saved: true,
      featured: false
    },
    {
      id: 6,
      category: 'Market Trends',
      title: 'Memory Prices Stabilizing After Year-Long Decline',
      summary: 'DRAM and NAND flash prices showing signs of bottoming out, recovery expected in H2 2025.',
      source: 'TrendForce',
      time: '3 days ago',
      impact: 'Positive',
      relevantComponents: ['Memory', 'DRAM', 'Flash'],
      saved: false,
      featured: false
    }
  ];

  const categories = [
    { id: 'all', label: 'All News', icon: Newspaper },
    { id: 'Supply Chain', label: 'Supply Chain', icon: Factory },
    { id: 'Market Trends', label: 'Market Trends', icon: TrendingUp },
    { id: 'Technology', label: 'Technology', icon: Cpu },
    { id: 'Regulatory', label: 'Regulatory', icon: Globe }
  ];

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'Positive': return 'bg-green-100 text-green-800';
      case 'Warning': return 'bg-yellow-100 text-yellow-800';
      case 'Critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getImpactIcon = (impact) => {
    switch (impact) {
      case 'Positive': return <TrendingUp className="h-4 w-4" />;
      case 'Warning': return <AlertTriangle className="h-4 w-4" />;
      case 'Critical': return <Zap className="h-4 w-4" />;
      default: return <Globe className="h-4 w-4" />;
    }
  };

  const filteredNews = activeCategory === 'all' 
    ? newsItems 
    : newsItems.filter(n => n.category === activeCategory);

  const featuredNews = newsItems.filter(n => n.featured);

  return (
    <Layout title="Intelligence Feed" subtitle="Real-time market news and supply chain intelligence">
      <div className="space-y-6" data-testid="intelligence-feed-page">
        {/* Featured News */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {featuredNews.map((news) => (
            <Card 
              key={news.id}
              className="bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200 shadow-sm hover:shadow-xl transition-all cursor-pointer"
              data-testid={`featured-news-${news.id}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-3">
                  <Badge className="bg-purple-600 text-white">
                    <Sparkles className="h-3 w-3 mr-1" />
                    Featured
                  </Badge>
                  <Badge className={getImpactColor(news.impact)}>
                    {getImpactIcon(news.impact)}
                    <span className="ml-1">{news.impact}</span>
                  </Badge>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">{news.title}</h3>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{news.summary}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{news.source}</span>
                    <span>•</span>
                    <Clock className="h-3 w-3" />
                    <span>{news.time}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                      <Bookmark className={`h-4 w-4 ${news.saved ? 'fill-yellow-500 text-yellow-500' : 'text-gray-400'}`} />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                      <Share2 className="h-4 w-4 text-gray-400" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Category Tabs */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Button
                  key={cat.id}
                  variant={activeCategory === cat.id ? 'default' : 'outline'}
                  className={activeCategory === cat.id ? 'bg-green-600 hover:bg-green-700' : ''}
                  onClick={() => setActiveCategory(cat.id)}
                >
                  <cat.icon className="h-4 w-4 mr-2" />
                  {cat.label}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* News List */}
        <div className="space-y-4">
          {filteredNews.map((news) => (
            <Card 
              key={news.id}
              className="bg-white border-gray-200 shadow-sm hover:shadow-sm transition-all cursor-pointer"
              data-testid={`news-item-${news.id}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                    {news.category === 'Supply Chain' && <Factory className="h-6 w-6 text-gray-600" />}
                    {news.category === 'Market Trends' && <TrendingUp className="h-6 w-6 text-gray-600" />}
                    {news.category === 'Technology' && <Cpu className="h-6 w-6 text-gray-600" />}
                    {news.category === 'Regulatory' && <Globe className="h-6 w-6 text-gray-600" />}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{news.category}</Badge>
                        <Badge className={getImpactColor(news.impact)}>
                          {news.impact}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                          <Bookmark className={`h-4 w-4 ${news.saved ? 'fill-yellow-500 text-yellow-500' : 'text-gray-400'}`} />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                          <ExternalLink className="h-4 w-4 text-gray-400" />
                        </Button>
                      </div>
                    </div>
                    
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{news.title}</h3>
                    <p className="text-sm text-gray-600 mb-3">{news.summary}</p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{news.source}</span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {news.time}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {news.relevantComponents.slice(0, 3).map((comp, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-gray-50">
                            {comp}
                          </Badge>
                        ))}
                        {news.relevantComponents.length > 3 && (
                          <Badge variant="outline" className="text-xs bg-gray-50">
                            +{news.relevantComponents.length - 3}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* AI Summary */}
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-green-800 flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              1AAgent Market Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 leading-relaxed">
              Based on today's intelligence feed, there are <strong>2 positive developments</strong> for your supply chain 
              (TSMC expansion and STM32U5 launch), and <strong>2 items requiring attention</strong> (MLCC price increase 
              and Murata production disruption). Consider reviewing your capacitor inventory and exploring strategic buys 
              before Q2 price increases take effect.
            </p>
            <Button className="mt-4 bg-green-600 hover:bg-green-700 text-white">
              <Sparkles className="h-4 w-4 mr-2" />
              Ask 1AAgent for Details
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default IntelligenceFeed;
